import { format, parseISO } from 'date-fns';
import { IProductCardPriceValue } from 'telia-front-react';
import { Price, PriceEndDates, Prices } from '../content/ProductInterfaces';

export const EURO_CURRENCIES = ['EUR', '€', 'EURO'];
export const VAT_RATE = Date.now() < +new Date('2025-07-01') ? 1.22 : 1.24;

export const replaceDotWithComma = (price: string | number): string => {
  if (!price) {
    return '';
  }
  return price.toString().replace(/[.]/, ',');
};

export const replaceDotsAndFormatToTwoDecimalPlacesRoundingUp = (price: string | number): string => {
  return replaceDotWithComma(formatToTwoDecimalPlacesRoundingUp(price));
};

export const replaceDotsAndFormatToCustomDecimalPlaceRoundingUp = (price: string | number, decimalPlace: number): string => {
  return replaceDotWithComma(+formatDecimalPlacesRoundingUp(price, decimalPlace));
};

export const formatRoamingServicePrice = (price: string | number): string => {
  if (typeof price !== 'number' && !price) {
    return '';
  }
  price = price.toString().length <= 4 ? Number(price).toFixed(2) : Number(price).toString();
  return price.includes('.') ? replaceDotWithComma(price) : price;
};

export const formatToTwoDecimalPlacesRoundingUp = (price: string | number): string => {
  return formatDecimalPlacesRoundingUp(price, 2);
};

export const formatDecimalPlacesRoundingUp = (price: string | number, places: number) => {
  if (typeof price !== 'number' && !price) {
    return '';
  }
  const priceMultiplied = stripUnnecessaryDecimals(+price * Math.pow(10, places));
  price = Math.ceil(priceMultiplied) / Math.pow(10, places);
  return price.toFixed(places);
};

const stripUnnecessaryDecimals = (price: number) => {
  const priceParts = `${price}`.split('.');
  if (priceParts.length === 2 && priceParts[1].startsWith('0')) {
    return Number(priceParts[0]);
  }
  return price;
};

export const renderAddedVat = (translate: (key: string) => string) => {
  return ` ${translate('price.vat')}`;
};

export const renderCurrency = (isVatEnabled: boolean, hasValue: boolean, translate: (key: string) => string, currency: string, periodSuffix?: string) => {
  let renderedCurrency = !currency || EURO_CURRENCIES.includes(currency) ? '€' : currency;
  if (periodSuffix) {
    renderedCurrency += `/${periodSuffix}`;
  }
  if (hasValue && !isVatEnabled) {
    renderedCurrency += renderAddedVat(translate);
  }
  return renderedCurrency;
};

export const stripTrailingZeroes = (price: string): string => {
  if (!price) {
    return '';
  }
  return price.replace(new RegExp('[\\.,]00'), '');
};

export const formatPrice = (price: number): string => {
  return stripTrailingZeroes(replaceDotWithComma(formatToTwoDecimalPlacesRoundingUp(price)));
};

export const renderMonthlyPrice = (isVatEnabled: boolean, price: number, translate: (key: string) => string, currency?: string): string => {
  const formattedPrice = formatPrice(price);
  const renderedCurrency = renderCurrencyWithMonths(isVatEnabled, price, translate, currency);
  return formattedPrice.length ? `${formattedPrice} ${renderedCurrency}` : formattedPrice;
};

export const renderCurrencyWithMonths = (isVatEnabled: boolean, val: number, translate: (key: string) => string, currency: string) => {
  let renderedCurrency = !currency || EURO_CURRENCIES.includes(currency) ? `€/${translate('price.months')}` : currency;
  if (val !== 0 && !isVatEnabled) {
    renderedCurrency += renderAddedVat(translate);
  }
  return renderedCurrency;
};

export const getApplicablePrice = (price: string, priceWithoutTax: string, vatEnabled: boolean): number => {
  let applicablePrice;
  if (!vatEnabled) {
    applicablePrice = asNumber(priceWithoutTax);
    if (applicablePrice == null) {
      applicablePrice = withoutTax(asNumber(price));
    }
  } else {
    applicablePrice = asNumber(price);
  }
  return applicablePrice;
};

export const withoutTax = (price: number): number => {
  if (price == null) {
    return null;
  }
  return price / VAT_RATE;
};

export const asNumber = (price: string): number => {
  if (!price) {
    return null;
  }
  return Number.parseFloat(price);
};

export const priceValue = (isVatEnabled: boolean, price: number, translate: (key: string) => string, currency?: string): IProductCardPriceValue | string => {
  const formattedPrice = formatPrice(price);
  const renderedCurrency = renderCurrency(isVatEnabled, price !== 0, translate, currency);
  return formattedPrice.length ? { value: `${formattedPrice}`, unit: renderedCurrency } : formattedPrice;
};

export enum FinanceType {
  LEASE = 'Lease',
  UPGRADE = 'Upgrade',
  MOBILE_LIFE = 'MobileLife',
  TELIA_1 = 'Telia1',
  OPERATING_LEASE = 'OperatingLease',
  CAPITAL_LEASE = 'CapitalLease'
}

export const getFinanceTypeTranslation = (
  clientType: string,
  financeType: FinanceType,
  translate: (key: string) => string,
  isCampaign?: boolean,
  interestRate?: string,
  hasTelia1?: boolean
): string => {
  if (clientType === 'business' && financeType === FinanceType.MOBILE_LIFE) {
    return translate('search.productCard.pricePeriod.business');
  } else if (hasTelia1 && interestRate === '0.00') {
    return translate('search.productCard.pricePeriod.Telia1');
  }
  return translate(`search.productCard.pricePeriod${isCampaign ? '.campaign' : ''}.${financeType}`);
};

export const addColon = (key: string): string => {
  return key && key.endsWith(':') ? key : `${key}:`;
};

export const shouldCrossOutDiscountPrice = (prices: Prices): boolean => prices?.previouslyBestPrice && prices?.currentPrice && !prices.currentPrice.clientPrice;

export const resolvePriceEndDate = (price: Price): PriceEndDates => ({
  isoDate: price.endDate,
  formattedDate: price.endDate ? format(parseISO(price.endDate), 'd.MM.yyyy') : undefined
});

export const calculateDiscountPrice = ({ currentPrice, previouslyBestPrice }: Prices): number => {
  return Math.floor(+previouslyBestPrice.amount - +currentPrice.amount);
};

export const calculateDiscountPercentage = ({ currentPrice, previouslyBestPrice }: Prices): number => {
  return Math.floor(((+previouslyBestPrice.amount - +currentPrice.amount) / +previouslyBestPrice.amount) * 100);
};

export const mobileLifeTotalPrice = (distributedPrice: number, callsPrice: number, deviceCount: number) => {
  return deviceCount * callsPrice + distributedPrice;
};

export const mobileLifePricePerUser = (totalPrice: number, deviceCount: number) => {
  return totalPrice / deviceCount;
};

export const getPrivateMobileLifeBasePrice = (
  selectedDeviceCount: number,
  personalPackagePrice: number,
  multiUserDistributedPrice: number,
  singleUserDistributedPrice: number,
  customDistributedPrices: number[],
  hasDiscount: boolean
): number => {
  if (hasDiscount) {
    return personalPackagePrice;
  }
  const defaultDistributedPrice = selectedDeviceCount > 1 ? multiUserDistributedPrice || singleUserDistributedPrice : singleUserDistributedPrice;
  const hasCustomSetDistributedPriceForPackage = customDistributedPrices?.length === 5 && !!customDistributedPrices[selectedDeviceCount - 1];
  return hasCustomSetDistributedPriceForPackage ? customDistributedPrices[selectedDeviceCount - 1] : defaultDistributedPrice;
};
